import type { SeoConfig } from './seo.type';

interface SeoDefaults {
  /** 예: 'HOWCOST' — 모든 페이지 title 끝에 붙임 */
  siteName: string;
  /** 예: '%s | HOWCOST' */
  titleTemplate?: string;
  /** 예: 'https://howcost.com' — canonical/og:url 만들 때 prefix로 사용 */
  baseUrl?: string;
  defaultDescription?: string;
  defaultOgImage?: string;
  defaultLocale?: string;
  /** false 면 페이지 설정과 무관하게 noindex,nofollow (운영 외 환경) */
  indexable?: boolean;
}

/**
 * 사이트 공통 SEO 기본값을 묶어두는 빌더.
 * 라우트별로 부분만 덮어쓰고, 나머지는 자동 머지된다.
 *
 * apps/web에서 한 번만 만들어 export하고 페이지마다 buildSeo({...}) 호출.
 */
export const createSeoBuilder = (defaults: SeoDefaults) => {
  const {
    siteName,
    titleTemplate = `%s | ${siteName}`,
    baseUrl,
    defaultDescription,
    defaultOgImage,
    defaultLocale = 'ko_KR',
    indexable = true,
  } = defaults;

  // SNS 봇은 상대경로 이미지를 못 읽는다.
  const toAbsolute = (url?: string) =>
    url && baseUrl && url.startsWith('/') ? `${baseUrl}${url}` : url;

  return (config: SeoConfig & { path?: string }): SeoConfig => {
    const { path, ...rest } = config;
    const absoluteUrl = path && baseUrl ? `${baseUrl}${path}` : config.canonical;
    const ogImage = toAbsolute(rest.og?.image ?? defaultOgImage);

    return {
      ...rest,
      titleTemplate: rest.titleTemplate ?? titleTemplate,
      description: rest.description ?? defaultDescription,
      canonical: rest.canonical ?? absoluteUrl,
      robots: indexable ? rest.robots : 'noindex,nofollow',
      og: {
        siteName,
        locale: defaultLocale,
        url: absoluteUrl,
        ...rest.og,
        image: ogImage,
      },
      twitter: {
        card: 'summary_large_image',
        ...rest.twitter,
        image: toAbsolute(rest.twitter?.image) ?? ogImage,
      },
    };
  };
};

export type SeoBuilder = ReturnType<typeof createSeoBuilder>;
